import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { catchError, of, throwError } from 'rxjs';

/**
 * The published Business Starter catalog, in the shape CI is meant to regenerate as `catalog.json`
 * next to the bundles (see `business-starters-design.md` in processpuzzle-core).
 *
 * CI publishes it to the environment's `processpuzzle-starters` bucket, and this application's NgInx
 * serves that bucket read-only under `/starters/` (the dev server proxies the same path to the local
 * MinIO). Only `STARTER_CATALOG_URL` knows that.
 */
export interface StarterCatalog {
  catalogVersion: number;
  starters: CatalogStarter[];
}

/** One starter, as its manifest names it, with every version ever published. */
export interface CatalogStarter {
  id: string;
  name: string;
  description: string;
  author?: string;
  category?: string;
  versions: StarterVersion[];
}

/**
 * `publishedAt` is an ISO date. The manifest has no such field; publishing a bundle stamps it.
 * `status` is optional only so a hand-written catalog reads as published.
 */
export interface StarterVersion {
  version: string;
  publishedAt: string;
  status?: StarterVersionStatus;
}

/** A yanked version stays in the bucket for organizations that applied it, but is no longer offered. */
export type StarterVersionStatus = 'published' | 'deprecated' | 'yanked';

/** What the catalog table shows: a starter reduced to its newest version. */
export interface StarterListing {
  id: string;
  name: string;
  description: string;
  latest: StarterVersion;
}

export const STARTER_CATALOG_URL = '/starters/catalog.json';

/**
 * Answers a 404 on the catalog with an empty one. A bucket nobody has published to yet holds no
 * `catalog.json`, and that is a registry without starters rather than a failure — the publish
 * workflow reads the same 404 the same way. It has to sit inside `centralHttpErrorInterceptor`,
 * which would otherwise report it before the page could tell the visitor "none yet".
 */
export const emptyStarterCatalogOnNotFound: HttpInterceptorFn = (request, next) =>
  request.url !== STARTER_CATALOG_URL
    ? next(request)
    : next(request).pipe(
        catchError((error: unknown) =>
          error instanceof HttpErrorResponse && error.status === 404
            ? of(new HttpResponse<StarterCatalog>({ status: 200, url: request.url, body: { catalogVersion: 1, starters: [] } }))
            : throwError(() => error),
        ),
      );

/**
 * Orders `major.minor.patch` numerically, so `1.10.0` is newer than `1.9.0`. A pre-release suffix
 * sorts before its release, which is all a catalog listing the newest version needs.
 */
export function compareVersions(left: string, right: string): number {
  const [leftCore, leftPre] = left.split('-', 2);
  const [rightCore, rightPre] = right.split('-', 2);
  const leftParts = leftCore.split('.').map(Number);
  const rightParts = rightCore.split('.').map(Number);
  for (let index = 0; index < Math.max(leftParts.length, rightParts.length); index++) {
    const difference = (leftParts[index] ?? 0) - (rightParts[index] ?? 0);
    if (difference !== 0) return difference;
  }
  if (leftPre === rightPre) return 0;
  if (leftPre === undefined) return 1;
  if (rightPre === undefined) return -1;
  return leftPre.localeCompare(rightPre);
}

/** Starters without an offered (not yanked) version are left out: there is nothing to install. Sorted by name. */
export function toListings(catalog: StarterCatalog | undefined): StarterListing[] {
  return (catalog?.starters ?? [])
    .map((starter) => ({ starter, offered: starter.versions.filter((version) => version.status !== 'yanked') }))
    .filter(({ offered }) => offered.length > 0)
    .map(({ starter, offered }) => ({
      id: starter.id,
      name: starter.name,
      description: starter.description,
      latest: offered.reduce((newest, candidate) => (compareVersions(candidate.version, newest.version) > 0 ? candidate : newest), offered[0]),
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
}
